'use client';

import { HiCheck } from '@react-icons/all-files/hi/HiCheck';
import Image from 'next/image';
import React from 'react';
import { IAlternative, IAlternativeListItem } from '../../types';
import { StyledDiv } from './alternativeContent.styles';

interface IAlternativeContentProps {
    content: IAlternative['content'];
    /** Light CTA block, used with heroVariant: "light". */
    light?: boolean;
}

export const AlternativeContent = ({ content, light = false }: IAlternativeContentProps) => {

    function returnSymbol(symbol: string) {
        if (symbol === 'yes') return <HiCheck className='checked' />;
        if (symbol === 'no') return renderX();
        return renderNone();
    }

    function renderContent(section: IAlternativeContentProps['content'][0], idx: number) {
        if (section.type === 'summary') {
            return (
                <div key={`summary-${idx}`} className="summary-box">
                    {section.title &&
                        <h2>{section.title}</h2>
                    }
                    {section.text &&
                        <div className="summary-text" dangerouslySetInnerHTML={{ __html: section.text }}></div>
                    }
                </div>
            );
        } else if (section.type === 'alternativesList') {
            const items = section.items || [];
            return (
                <div key={`altlist-${idx}`} className="alt-list-section">
                    {section.title &&
                        <h2>{section.title}</h2>
                    }
                    {section.text &&
                        <div className="p" dangerouslySetInnerHTML={{ __html: section.text }}></div>
                    }
                    <div className="alt-cards">
                        {items.map((item, i) => (
                            <article key={`alt-card-${i}`} className={`alt-card${item.featured ? ' featured' : ''}`}>
                                <div className="alt-card-head">
                                    {typeof item.rank === 'number' &&
                                        <span className="alt-rank" aria-hidden="true">{item.rank}</span>
                                    }
                                    <h3>
                                        {item.href
                                            ? <a href={item.href}>{item.name}</a>
                                            : item.name}
                                    </h3>
                                    {item.featured &&
                                        <span className="alt-pick">Top pick</span>
                                    }
                                </div>
                                {item.bestFor &&
                                    <p className="alt-best-for"><span>Best for:</span> {item.bestFor}</p>
                                }
                                {item.description &&
                                    <div className="alt-desc" dangerouslySetInnerHTML={{ __html: item.description }}></div>
                                }
                                {item.standout &&
                                    <p className="alt-standout"><span>Standout:</span> {item.standout}</p>
                                }
                                {((item.pros && item.pros.length) || (item.cons && item.cons.length)) &&
                                    <div className="alt-proscons">
                                        {item.pros && item.pros.length > 0 &&
                                            <ul className="alt-pros">
                                                {item.pros.map((p, pi) => <li key={`pro-${pi}`}>{p}</li>)}
                                            </ul>
                                        }
                                        {item.cons && item.cons.length > 0 &&
                                            <ul className="alt-cons">
                                                {item.cons.map((c, ci) => <li key={`con-${ci}`}>{c}</li>)}
                                            </ul>
                                        }
                                    </div>
                                }
                                {(item.featured && item.href) &&
                                    <a className="alt-cta" href={item.href}>{item.ctaLabel || 'Start for Free'}</a>
                                }
                            </article>
                        ))}
                    </div>
                    <AlternativesListSchema title={section.title} items={items} />
                </div>
            );
        } else if (section.type === 'comparisonMatrix') {
            const matrix = section.matrix;
            if (!matrix) return null;
            return (
                <div key={`matrix-${idx}`} className="matrix-section">
                    {section.title &&
                        <h2>{section.title}</h2>
                    }
                    {section.text &&
                        <div className="p" dangerouslySetInnerHTML={{ __html: section.text }}></div>
                    }
                    <div className="matrix-scroll">
                        <table className="matrix-table">
                            <thead>
                                <tr>
                                    {matrix.headers.map((h, hi) => (
                                        <th key={`mh-${hi}`} className={matrix.highlightCol === hi ? 'highlight-col' : ''}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {matrix.rows.map((row, ri) => (
                                    <tr key={`mr-${ri}`} className={matrix.highlightRow === ri ? 'highlight-row' : ''}>
                                        <th scope="row">{row.feature}</th>
                                        {row.values.map((v, vi) => (
                                            <td key={`mc-${ri}-${vi}`} className={matrix.highlightCol === vi + 1 ? 'highlight-col' : ''}>{v}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        } else if (section.type === 'cta') {
            return (
                <div key={`cta-${idx}`} className={`cta-block${light ? ' light' : ''}`}>
                    {section.title &&
                        <h2>{section.title}</h2>
                    }
                    {section.text &&
                        <div className="cta-text" dangerouslySetInnerHTML={{ __html: section.text }}></div>
                    }
                    {section.ctaLabel &&
                        <a className="cta-btn" href={section.ctaHref || '/widgets'}>{section.ctaLabel}</a>
                    }
                </div>
            );
        } else if (section.type === 'text') {
            return (
                <div key={`text-${section.title || ''}-${section.text || ''}-${idx}`} className="text-section">
                    {section.title &&
                        <h3>{section.title}</h3>
                    }
                    {section.text &&
                        <div className="p" dangerouslySetInnerHTML={{ __html: section.text }}></div>
                    }
                </div>
            );
        } else if (section.type === 'banner') {
            return (
                <div key={`banner-${section.title || ''}-${idx}`} className={`card-helper-wrapper ${section.bannerStyle}`}>
                    <div className={`banner ${section.bannerAlign}`}>
                        {section.title &&
                            <h3>{section.title}</h3>
                        }
                        {section.bannerType === 'trustpilot' &&
                            <div className="trustpilot-container">
                                {/* <CustomLink variant='secondary' href='https://www.trustpilot.com/review/commoninja.com' target={'_blank'} external={true}> */}
                                <p>Trustpilot</p>
                                {/* </CustomLink> */}
                                {/* <CustomLink variant='secondary' href='https://www.trustpilot.com/review/commoninja.com' target={'_blank'} external={true}> */}
                                <Image src={'/assets/trustpilot.png'} alt='Trustpilot Rating' width={215} height={30} title='4.3 stars out of 5' />
                                {/* </CustomLink> */}
                            </div>
                        }
                        {(section.text && section.bannerType !== 'trustpilot') &&
                            <div className="banner-text" dangerouslySetInnerHTML={{ __html: section.text }}></div>
                        }
                    </div>
                </div>
            );
        } else {
            return (
                <div key={`table-${section.title || ''}-${idx}`} className="table-wrapper">
                    {section.title &&
                        <h3>{section.title}</h3>
                    }
                    {section.text &&
                        <div className="p" dangerouslySetInnerHTML={{ __html: section.text }}></div>
                    }
                    <table >
                        <thead>
                            <tr>
                                {section.showFeatures &&
                                    <th></th>
                                }
                                <th className='first-td'>{section.tableHeader1}</th>
                                <th>{section.tableHeader2}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {section.tableRows.map((row, idx) => {
                                if (row.type === 'symbol') {
                                    return (
                                        <tr className={row.tableType || ''} key={`row-${idx}`}>
                                            {section.showFeatures &&
                                                <td>{row.featureName || ''}</td>
                                            }
                                            <td className='first-td'>
                                                {returnSymbol(row.header1Symbol || '')}
                                                {row.header1SymbolNote &&
                                                    <p className='note'>
                                                        {row.header1SymbolNote}
                                                    </p>
                                                }
                                            </td>
                                            <td>
                                                {returnSymbol(row.header2Symbol || '')}
                                                {row.header2SymbolNote &&
                                                    <p className='note'>
                                                        {row.header2SymbolNote}
                                                    </p>
                                                }
                                            </td>
                                        </tr>
                                    );
                                }

                                return (
                                    <tr className={row.tableType || ''} key={`row-${idx}`}>
                                        {section.showFeatures &&
                                            <td>{row.featureName || ''}</td>
                                        }
                                        <td className='first-td' dangerouslySetInnerHTML={{ __html: row.header1Text || '' }}></td>
                                        <td dangerouslySetInnerHTML={{ __html: row.header2Text || '' }}></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            );
        }

    }
    return (
        <StyledDiv>
            {content.map((section, idx) => {
                return renderContent(section, idx);
            })}
        </StyledDiv>
    );
};

function AlternativesListSchema({ title, items }: { title?: string; items: IAlternativeListItem[]; }) {
    if (!items || !items.length) return null;
    const stripHtml = (s?: string) => (s || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: title || 'Alternatives',
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        numberOfItems: items.length,
        itemListElement: items.map((item, i) => ({
            '@type': 'ListItem',
            position: item.rank || i + 1,
            name: item.name,
            ...(item.href ? { url: item.href } : {}),
            ...(item.description || item.bestFor
                ? { description: [item.bestFor ? `Best for: ${item.bestFor}` : '', stripHtml(item.description)].filter(Boolean).join('. ') }
                : {}),
        })),
    };
    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
    );
}

function renderX() {
    return (
        <svg className='x' width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.3578 3.74235L12.6009 9.49987L18.3578 15.2571C19.2141 16.1138 19.2141 17.5014 18.3578 18.3581C17.9299 18.7859 17.369 19 16.8083 19C16.2467 19 15.6857 18.7862 15.2582 18.3581L9.50003 12.6001L3.74232 18.358C3.31452 18.7859 2.75352 18.9999 2.19236 18.9999C1.63136 18.9999 1.07074 18.7862 0.642559 18.358C-0.21375 17.5017 -0.21375 16.114 0.642559 15.2571L6.39928 9.49981L0.642232 3.74235C-0.214077 2.88603 -0.214077 1.49806 0.642232 0.641745C1.49838 -0.213915 2.88552 -0.213915 3.74199 0.641745L9.49997 6.39927L15.2575 0.641745C16.1142 -0.213915 17.5015 -0.213915 18.3574 0.641745C19.2141 1.49806 19.2141 2.88603 18.3578 3.74235Z" fill="#A9A9A9" />
        </svg>
    );
}

function renderNone() {
    return (
        <svg width="8" height="18" viewBox="0 0 8 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.95897 7.99532L7.43314 2.49243C7.65245 1.67396 7.16686 0.832905 6.34819 0.613707C5.53015 0.394678 4.6895 0.88003 4.46976 1.69822L2.9952 7.20133L2.20103 10.1649L0.72687 15.6676C0.507793 16.4865 0.99328 17.3273 1.81173 17.5466C2.22099 17.6563 2.63549 17.5897 2.97544 17.3934C3.31548 17.1971 3.58052 16.8711 3.69006 16.4622L5.16461 10.9588L5.95897 7.99532Z" fill="#D9D9D9" />
        </svg>
    );
}